import { escapeHTML } from '../../utils/escape.js';
import { ACCIONES } from '../../shared/timesheet-policy.js';

/**
 * Teclado numérico de PIN (time-clock-kiosk spec "PIN-Validated Server-
 * Stamped Clock Action"). Render puro; el `<input name="pin">` es SIEMPRE de
 * solo-escritura (nunca interpola el PIN tipeado, mismo patrón que
 * `EmpleadosView#renderPinDialog`) — los dígitos entran por los botones del
 * teclado (o el teclado físico, Kiosk UX follow-up 2026-09-02), que
 * `js/kiosco.js` escribe directo en `input.value` (propiedad, nunca
 * serializada acá). `readonly` bloquea el teclado físico/OSK del navegador
 * (el input sigue sin aceptar foco de escritura — el teclado físico entra
 * por un listener de `keydown` en `js/kiosco.js`, no por este input).
 *
 * Indicador de dígitos tipeados: fila de `MAX_PIN_LEN` puntos
 * (`[data-pin-dot]`), SIEMPRE renderizados vacíos acá (el diálogo abre con
 * el PIN en blanco) — `js/kiosco.js` los llena/vacía vía `classList`
 * (nunca vuelve a llamar a este render por cada dígito, mismo motivo que el
 * input write-only: nada del PIN tipeado pasa nunca por HTML interpolado).
 * `MAX_PIN_LEN` se exporta desde acá y lo importa `js/kiosco.js` (Kiosk UX
 * follow-up, review-b590ffce9e134a5e finding 4) — antes eran dos literales
 * `8` separados (`PIN_DOTS_COUNT` acá, `MAX_PIN_LEN` allá) sincronizados
 * solo por comentario; ahora hay una única fuente de verdad.
 *
 * Kiosk UX round 2 (2026-09-02, owner requests 1 + 4): tablet-scale pass —
 * teclado más compacto (`py-1.5`/`text-sm`/`gap-1.5`), diálogo más angosto
 * (`p-5`/`max-w-[15rem]`), botones Cancelar/Confirmar más chicos
 * (`py-2.5`/`text-[10px]`), y puntos de PIN más grandes/visibles
 * (`w-4 h-4`/`gap-2.5`) — el estado lleno (`bg-brand`/`border-brand`) contra
 * vacío (solo borde) es el mismo, solo el tamaño cambió.
 */

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'borrar'];
/**
 * Cantidad máxima de dígitos de un PIN — también cuántos puntos se dibujan.
 * Owner decision 2026-09-02: el PIN es de EXACTAMENTE 4 dígitos (antes 4-8).
 * `admin-empleados.service.js#PIN_FORMAT`, la validación de `api/kiosco.js`
 * y el formulario de Reportes (`staff-reportes.service.js#validarReporte`,
 * `StaffReportesView.js`; tests/unit/reportes-parity.test.js) deben
 * mantenerse en sync con este valor — no hay una fuente compartida
 * entre `js/` y `api/` para una sola constante numérica (mismo criterio que
 * `ACCIONES` antes de unificarse: acá directamente no se duplicó porque el
 * valor no cambia con la misma frecuencia que un enum de acciones).
 */
export const MAX_PIN_LEN = 4;

function keypadButton(key) {
    if (key === '') return '<span></span>';
    if (key === 'borrar') {
        return '<button type="button" data-action="kiosco-pin-borrar" aria-label="Borrar" class="py-1.5 rounded-2xl border border-border-subtle text-text-muted"><i class="fa-solid fa-delete-left" aria-hidden="true"></i></button>';
    }
    return `<button type="button" data-action="kiosco-pin-digito" data-digit="${key}" class="py-1.5 rounded-2xl border border-border-subtle text-sm font-black text-text-primary active:scale-95 transition-transform">${key}</button>`;
}

function pinDots() {
    const dot = '<span data-pin-dot class="w-4 h-4 rounded-full border border-border-subtle transition-colors"></span>';
    return `<div data-pin-dots aria-hidden="true" class="flex justify-center gap-2.5 mb-4">${dot.repeat(MAX_PIN_LEN)}</div>`;
}

/**
 * @param {{ empleado: { id: string, nombre: string }, accion?: 'entrada'|'salida', error?: string }|null} pinState `null` = cerrado.
 * `accion` nombra el título del diálogo (Kiosk UX round 2, owner request 3)
 * — decidido por `js/kiosco.js` al abrir, según el `trabajando` actual del
 * empleado. Default a `'entrada'` solo por robustez (no debería faltar).
 */
export function PinPad(pinState) {
    if (!pinState?.empleado) return '';

    const { empleado, accion = ACCIONES.ENTRADA, error } = pinState;
    const accionLabel = accion === ACCIONES.SALIDA ? 'Salida' : 'Entrada';
    const errorHtml = error
        ? `<p class="text-xs text-red-500 font-bold mt-4 text-center" role="alert">${escapeHTML(error)}</p>`
        : '';

    return `
        <div role="dialog" aria-modal="true" aria-labelledby="kiosco-pin-title" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-6">
            <div class="bg-base rounded-[2rem] p-5 max-w-[15rem] w-full shadow-2xl">
                <p id="kiosco-pin-title" class="text-sm font-bold text-text-primary text-center mb-6">${accionLabel} de ${escapeHTML(empleado.nombre)}</p>
                <form data-id="${escapeHTML(empleado.id)}">
                    <input type="password" name="pin" inputmode="numeric" autocomplete="off" readonly aria-label="PIN" class="sr-only" />
                    ${pinDots()}
                    <div class="grid grid-cols-3 gap-1.5">${DIGITS.map(keypadButton).join('')}</div>
                    ${errorHtml}
                    <div class="flex gap-3 mt-6">
                        <button type="button" data-action="kiosco-pin-cancelar" class="flex-1 py-2.5 rounded-2xl border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted">Cancelar</button>
                        <button type="submit" data-action="kiosco-pin-confirmar" disabled class="flex-1 py-2.5 rounded-2xl bg-brand text-white text-[10px] font-black uppercase tracking-widest shadow-lg disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed">Confirmar</button>
                    </div>
                </form>
            </div>
        </div>
    `;
}
